/* ====================================================================
   MES CART V3 (H61) — cart drawer interactions
   Layered ON TOP of the theme's cart machinery (cart-drawer element,
   LineItemQuantity, free-shipping-bar, wrap-all gift machinery).
   Never mutates checkout routing. All listeners are delegated on the
   persistent <cart-drawer> element / document, so they survive the
   drawer's innerHTML re-renders.
   ==================================================================== */
(function () {
  'use strict';

  var drawer = document.getElementById('cart-drawer');
  if (!drawer || !drawer.classList.contains('mcart')) return;

  /* ---- dark-mode sync ----
     The V3 master-colour engine (main-product.liquid) sets --w3-page on html:root;
     on dark product templates it's a dark hex. Mirror the pill header's isPageDark so
     the cart drawer goes dark on those pages, exactly like the menu drawer — the class
     is only ADDED on dark pages, so the light path is untouched. Runs at init (page
     darkness is fixed per load; this deferred script runs after main-product's inline
     override has parsed), so .mcart--dark is present before the drawer is ever opened. */
  function mcartLuminance(str) {
    if (!str) return null;
    str = str.trim();
    var r, g, b, m;
    if ((m = str.match(/^rgba?\(([^)]+)\)/))) {
      var p = m[1].split(',').map(function (v) { return parseFloat(v); });
      if (p.length >= 4 && p[3] === 0) return null;
      r = p[0]; g = p[1]; b = p[2];
    } else if (str[0] === '#') {
      var h = str.slice(1);
      if (h.length === 3) h = h.replace(/(.)/g, '$1$1');
      if (h.length < 6) return null;
      r = parseInt(h.slice(0, 2), 16); g = parseInt(h.slice(2, 4), 16); b = parseInt(h.slice(4, 6), 16);
    } else { return null; }
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  }
  function syncDarkMode() {
    var L = mcartLuminance(getComputedStyle(document.documentElement).getPropertyValue('--w3-page'));
    var pageDark = L !== null && L < 0.5;
    // The overlay pill header (homepage + other overlay pages) puts .mp3-dark on its section when it
    // sits over a dark hero — the header/menu is dark while the PAGE tokens are still light. Make the
    // cart agree: go dark, and since the page tokens are light, inject a self-contained dark
    // --w3-page/--w3-ink on the cart element so the derived .mcart--dark skin renders dark. On a
    // genuinely dark page (pageDark) we keep the real tokens so the cart matches the page bg.
    // (Shrey, 20 Jul 2026.)
    var headerDark = !!document.querySelector('.mes-pillhead-sec.mp3-dark');
    var dark = pageDark || headerDark;
    if (dark && !pageDark) {
      // Match the pill MENU sheet's dark palette exactly (.mp3-dark .mp3-sheet-panel =
      // rgba(24,22,20,.86) over #181614, text #F2ECE0) so the cart + menu look cohesive on
      // the dark homepage (Shrey, 20 Jul 2026).
      drawer.style.setProperty('--w3-page', '#181614');
      drawer.style.setProperty('--w3-ink', '#F2ECE0');
    } else {
      drawer.style.removeProperty('--w3-page');
      drawer.style.removeProperty('--w3-ink');
    }
    drawer.classList.toggle('mcart--dark', dark);
  }
  syncDarkMode();
  // The header's dark state is scroll-dependent, so re-evaluate whenever the cart opens/closes.
  drawer.addEventListener('toggle', syncDarkMode);

  /* ---- mobile first-tap close (Shrey, 21 Jul 2026) ----
     On TOUCH the native DialogElement routes overlay / outside-close taps through focus-trap's
     _allowOutsideClickTouch (theme.js): it returns false and defers to a re-armed once-`touchend`
     listener, so the FIRST tap on the dim overlay AND on the round close button is swallowed and
     the sheet only closes on the SECOND tap. Bypass that on the CART ONLY: a direct pointerup on
     the shadow [part="overlay"] + [part="outside-close-button"] closes on the first tap. hide()
     is idempotent (guards on the `open` attr), so this never double-fires or fights the native
     close path. Touch-only (pointerType !== 'mouse') so desktop mouse keeps the native behaviour
     verbatim. Never touches the shared theme.js DialogElement. */
  var _mcartCloseBound = false;
  function bindMobileFirstTapClose() {
    if (_mcartCloseBound) return;
    var sr = drawer.shadowRoot;
    if (!sr) return;
    var overlay = sr.querySelector('[part="overlay"]');
    var closeBtn = sr.querySelector('[part="outside-close-button"]');
    if (!overlay && !closeBtn) return;
    _mcartCloseBound = true;
    function firstTapClose(e) {
      if (e.pointerType === 'mouse') return;      // desktop mouse keeps the native path
      if (!drawer.hasAttribute('open')) return;   // ignore stray events while closed
      drawer.hide();
      // The tap's synthesized `click` fires AFTER hide() starts, when the drawer no longer
      // hit-tests — so it lands on whatever sits underneath (homepage slideshow link opened a
      // new page; Shrey 21 Jul). Swallow exactly one click within the ghost-click window.
      var kill = function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        document.removeEventListener('click', kill, true);
      };
      document.addEventListener('click', kill, true);
      setTimeout(function () { document.removeEventListener('click', kill, true); }, 500);
    }
    if (overlay) overlay.addEventListener('pointerup', firstTapClose);
    if (closeBtn) closeBtn.addEventListener('pointerup', firstTapClose);
  }
  bindMobileFirstTapClose();
  // The shadow parts may upgrade after this deferred script parses; re-try on the first open.
  drawer.addEventListener('toggle', bindMobileFirstTapClose);

  /* Toggle .mcart--empty on the drawer when the empty state is showing, so the empty-only
     centring CSS applies WITHOUT touching the filled cart's body layout/width. Re-checked on
     every re-render (the drawer swaps between empty <-> filled markup on add/remove). */
  function syncEmptyClass() {
    drawer.classList.toggle('mcart--empty', !!drawer.querySelector('.mcart-empty'));
  }
  syncEmptyClass();

  var lastCart = null;        // latest cart JSON (cart.js shape)
  var pendingRemove = null;   // { key, item:{id,quantity,properties,selling_plan}, prevSavings, title }
  var activeToast = null;     // { message, expiresAt } — survives drawer re-renders
  var toastTimer = null;
  var effTimer = null;
  var recentLoaded = false;
  var cardBusy = false;
  var wrapPending = null;     // intended wrap on/off while a toggle is in flight; null = settled
  var wrapPendingAt = 0;      // when the intent was latched — see the expiry in syncWrapZone (TA-41958 r4)
  var wrapQtySynced = null;   // last wrap quantity WE wrote, so the re-level pass can't loop (TA-41958)

  /* ---------------- helpers ---------------- */

  function fmtMoney(paise) {
    var r = Math.round(paise / 100);
    return '₹' + r.toLocaleString('en-IN');
  }

  function addonsEl() { return drawer.querySelector('[data-mcart-addons]'); }

  function cfg(name, fallback) {
    var el = addonsEl();
    var v = el ? el.getAttribute(name) : null;
    return (v === null || v === '') ? fallback : v;
  }

  function rate() {
    /* NO SILENT FALLBACK (14 Aug 2026, Freedom Sale). This used to default to 15
       and, on any bad value, assert 0.15. That number is not only printed as copy —
       syncEffPanels() computes DISPLAYED PRICES from it. During a 30% sale a missing
       attribute would have quietly shown every shopper a 15% discounted price and an
       offer message to match: plausible, wrong, and impossible to notice.
       It now returns 0, meaning "unknown", and both callers skip rather than guess. */
    var r = parseFloat(cfg('data-mcart-rate', ''));
    return (isNaN(r) || r <= 0 || r >= 100) ? 0 : r / 100;
  }

  function specialHandles() {
    var out = [];
    var wrapZone = drawer.querySelector('[data-mcart-wrapzone]');
    var wh = cfg('data-mcart-wrap-handle', wrapZone ? wrapZone.getAttribute('data-mcart-wrap-handle') : '');
    var gh = cfg('data-mcart-gift-handle', '');
    if (wh) out.push(wh);
    if (gh) out.push(gh);

    /* OFFER EXCLUSIONS (Shrey, 14 Aug 2026). Ranges running their own lower offer —
       rakhis on 15% while the store is on 30% — must not count toward this offer.
       Liquid works out which cart lines are excluded (it can see product.collections,
       /cart.js cannot) and writes the handles into a marker inside the line-items
       region, so this list re-renders with the cart. Reusing the existing skip list
       means every consumer — the unit count, the eligible totals, the effective-price
       panels — excludes them without a second mechanism to keep in sync. */
    var marker = drawer.querySelector('[data-mcart-excluded]');
    var raw = marker ? marker.getAttribute('data-mcart-excluded') : '';
    if (raw) {
      raw.split(',').forEach(function (h) {
        h = h.trim();
        if (h && out.indexOf(h) === -1) out.push(h);
      });
    }
    return out;
  }

  // count of discount-eligible units (excludes gift wrap + free gift lines)
  function eligibleCount(cart) {
    if (!cart) return 0;
    var skip = specialHandles();
    return cart.items.reduce(function (n, it) {
      return skip.indexOf(it.handle) > -1 || (it.properties && it.properties._mes_gift) ? n : n + it.quantity;
    }, 0);
  }

  function eligibleOriginal(cart) {
    if (!cart) return 0;
    var skip = specialHandles();
    return cart.items.reduce(function (n, it) {
      return skip.indexOf(it.handle) > -1 ? n : n + it.original_line_price;
    }, 0);
  }

  // what the discount-eligible items actually cost right now (excludes wrap/free-gift lines,
  // so the marginal-cost math uses one consistent basis on both sides)
  function eligibleFinal(cart) {
    if (!cart) return 0;
    var skip = specialHandles();
    return cart.items.reduce(function (n, it) {
      return skip.indexOf(it.handle) > -1 ? n : n + it.final_line_price;
    }, 0);
  }

  function cartSavings(cart) {
    return cart ? (cart.original_total_price - cart.total_price) : 0;
  }

  /* ⛔ ONE TAP WAS FIRING SIXTEEN /cart.js READS (measured 6 Aug 2026, this browser, on RC4).
     Priyanka: "the scrolling issue is minimal until the gift wrap is interacted with… adding,
     removing, or otherwise interacting with the gift wrap significantly increases the lag."
     She was pointing straight at this. Counting every network call for 8s after ONE wrap tick:
       20 calls total — 16 × /cart.js, 2 × /cart/change.js, 13 mutation records, 62 nodes swapped.
     Nothing here is wrong individually: the sync batch, the MutationObserver batch, the
     `cart:refresh` handler, `bootCart` and `relevelWrap` (with up to 3 retries) each want a
     fresh cart, and each politely fetches its own. Together they stampede — on a 4G Android
     that is seconds of parse-and-reconcile churn layered over the gesture she is trying to make.

     TWO GUARDS, both conservative:
     • IN-FLIGHT SHARING — concurrent callers await the SAME request instead of opening their own.
     • A 250ms FRESHNESS WINDOW — a read that just completed is reused. Deliberately shorter than
       any cart write round-trip, so no caller can be handed a cart from before its own write;
       `force` bypasses it for the correction path that must see post-write truth.
     Neither changes what any caller receives, only how many times the network is asked. */
  var cartInFlight = null, cartFreshAt = 0;
  var CART_TTL = 250;
  function fetchCart(force) {
    if (!force && cartInFlight) return cartInFlight;
    if (!force && lastCart && (Date.now() - cartFreshAt) < CART_TTL) {
      return Promise.resolve(lastCart);
    }
    var p = fetchCartNet().then(function (c) { cartFreshAt = Date.now(); return c; });
    cartInFlight = p;
    p.then(function () { if (cartInFlight === p) cartInFlight = null; },
           function () { if (cartInFlight === p) cartInFlight = null; });
    return p;
  }
  function fetchCartNet() {
    // HARDENED (TA-41958 round 4): a challenged/failed /cart.js (bot protection returns an
    // HTML page with 429) used to throw out of r.json() and reject every sync pass
    // downstream. On a bad response keep the last known cart — stale truth beats a crash.
    return fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart.js')
      .then(function (r) {
        // Shopify serves cart.js as text/javascript, so require-json is wrong — HTML is the
        // failure signature (challenge/error pages). Reject non-OK or HTML; accept the rest.
        var ct = (r.headers.get('content-type') || '').toLowerCase();
        if (!r.ok || ct.indexOf('html') !== -1) throw new Error('cart.js HTTP ' + r.status);
        return r.json();
      })
      .then(function (c) { lastCart = c; return c; })
      .catch(function (e) {
        console.warn('[mcart] cart fetch failed, keeping last known cart:', e && e.message);
        return lastCart;
      });
  }

  /* ---------------- soft cart updates (stability pass, Shrey 24 Jul 2026) ----------------
     cart:refresh makes CartDrawer._onCartRefresh replace the ENTIRE drawer (header, quick-add
     row, everything) — that full rebuild was the "everything flashes" on gift-wrap toggle /
     undo / direct-add, and a rebuild landing mid-drag also killed scroll gestures. Instead:
     ask the cart endpoint to BUNDLE the drawer's section render (`sections` param) and dispatch
     cart:change — CartDrawer._onCartChanged then reconciles line rows IN PLACE and swaps only
     the prices/footer slots. Header + quick-add + wrap zone keep their DOM nodes. cart:refresh
     stays only as the error fallback. */

  function drawerSectionId() {
    var sec = drawer.closest('.shopify-section');
    return sec ? sec.id.replace('shopify-section-', '') : null;
  }

  function softCartChange(sectionsHtml) {
    var secId = drawerSectionId();
    // no bundled render (or no resolvable section id) -> the reconcile has nothing to diff
    // against; take the full-refresh path instead of dispatching a broken payload
    if (!secId || !sectionsHtml || !sectionsHtml[secId]) { hardRefresh(); return Promise.resolve(); }
    // V9.28.162: an add that asked for `mes-cart-json` (withCartJson below) carries the post-write
    // cart in its own response; use it instead of a cart read. Every other caller's sections have
    // no such entry, so parse() returns null and fetchCart() runs exactly as before.
    var fromAdd = window.MesCartJson ? window.MesCartJson.parse(sectionsHtml) : null;
    return (fromAdd ? Promise.resolve(fromAdd) : fetchCart()).then(function (c) {
      var cart = {};
      for (var k in c) cart[k] = c[k];
      cart.sections = sectionsHtml;
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'soft:update', cart: cart }
      }));
    });
  }

  // V9.28.162: the drawer's section id plus `mes-cart-json`, for the two in-drawer ADD paths
  // (undo, direct quick-add), so softCartChange can skip its cart read. A null id stays null, so
  // softCartChange still takes its hardRefresh branch exactly as before.
  function withCartJson(secId) {
    return secId && window.MesCartJson ? window.MesCartJson.withSection(secId) : secId;
  }

  function hardRefresh() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  /* ---------------- count label ---------------- */

  function syncCountLabel() {
    if (!lastCart) return;
    // The gift wrap line no longer renders as a card, so the header count excludes it too
    // (Shrey, 21 Jul 2026). The wrap handle is stamped on the drawer element itself.
    var wrapHandle = drawer.getAttribute('data-mcart-wrap-handle') || '';
    // Greeting cards are hidden from the list as well (Shrey, 20 Aug 2026), so they come out of
    // the live count for the same reason the wrap does: a count that includes a line the shopper
    // cannot see reads as a bug in the count.
    var cardHandles = drawer.getAttribute('data-mcart-card-handles') || '';
    var n = lastCart.items.reduce(function (acc, it) {
      if (wrapHandle && it.handle === wrapHandle) return acc;
      if (cardHandles && cardHandles.indexOf(',' + it.handle + ',') !== -1) return acc;
      return acc + it.quantity;
    }, 0);
    var num = drawer.querySelector('[data-mcart-count]');
    if (num) num.textContent = n;
    var label = drawer.querySelector('[data-mcart-count-label]');
    if (label) label.textContent = n === 1 ? 'item' : 'items';
  }

  /* (the "% offer applied" tier bar was removed 24 Jul 2026 — replaced by the pill switcher;
     the 1-item offer surface is the strip nudge) */

  /* ---------------- footer status (kept fresh between server renders) ---------------- */
  // The footer is server-rendered on every cart change, so no JS needed there.

  /* ---------------- marginal-cost panels ---------------- */

  /* V9.28.143 (H479), "cheaper half" offer (buy 1 get 1 at X%): what a card ADDS depends on what the cart
     already holds (the 2nd item is discounted only if it is the cheaper one; at an even count the next item
     gets nothing until another follows). So every card shows its plain price, and the green pill shows the
     exact amount it adds whenever that is less than the price: MesOffer.cost() on the cart's own unit prices.
     Also repaints cards rendered without the cart's offer kind (the recently-viewed pane). */
  function syncEffCheapest(mo) {
    drawer.querySelectorAll('.mcart-upcard').forEach(function (card) {
      var price = parseInt(card.getAttribute('data-mcart-price'), 10);
      if (isNaN(price)) return;
      /* V9.28.151: the card's price text is server-rendered (plain under this offer); the script no longer rewrites it,
         because fmtMoney is rupee-only and rounds */
      var panel = card.querySelector('[data-mcart-eff]');
      if (!panel) return;
      if (card.getAttribute('data-mcart-covered') === '0') { panel.hidden = true; return; }   /* not in the offer (flag always written since V9.28.151) */
      var eff = Math.max(0, Math.round(mo.cost([price])));
      if (eff >= price) { panel.hidden = true; return; }
      var amountEl = panel.querySelector('[data-mcart-eff-amount]');
      if (amountEl) amountEl.textContent = fmtMoney(eff);
      panel.hidden = false;
    });
  }

  function syncEffPanels() {
    if (!lastCart) return;
    var mo = window.MesOffer;
    if (mo && mo.kind === 'cheapest') { syncEffCheapest(mo); return; }
    var panels = drawer.querySelectorAll('[data-mcart-eff]');
    if (!panels.length) return;
    var r = rate();
    if (!r) return;   /* rate unknown — leave the server-rendered prices alone */
    var count = eligibleCount(lastCart);
    var orig = eligibleOriginal(lastCart);
    var current = eligibleFinal(lastCart);
    panels.forEach(function (panel) {
      var card = panel.closest('.mcart-upcard');
      var price = card ? parseInt(card.getAttribute('data-mcart-price'), 10) : NaN;
      // Show the "Only ₹X added to total" pill ONLY when there is exactly 1 eligible unit in
      // the cart (Shrey, 24 Jul 2026). At 2+ units the flat 15% is already applied, so adding
      // one more adds exactly its sticker price (price × 0.85) = the left "now" price — the pill
      // would just echo the left number, so hide it. At 0/1 the marginal genuinely differs.
      if (isNaN(price) || count !== 1) { panel.hidden = true; return; }
      // adding one unit makes eligible count >= 2, so the flat rate applies to everything;
      // wrap/free-gift lines are excluded from BOTH sides of the delta
      var eff = Math.max(0, Math.round((orig + price) * (1 - r)) - current);
      var amountEl = panel.querySelector('[data-mcart-eff-amount]');
      if (amountEl) amountEl.textContent = fmtMoney(eff);
      // the note is now a static "added to total" caption (Shrey, 24 Jul 2026 redesign)
      panel.hidden = false;
    });
  }

  /* ---------------- gift wrap row sync ---------------- */

  // units in the cart that can actually be gift wrapped (not the wrap product, not the
  // free gift, not an excluded product)
  function wrappableCount(zone) {
    if (!lastCart) return 0;
    var wrapHandle = zone.getAttribute('data-mcart-wrap-handle') || '';
    var giftHandle = zone.getAttribute('data-mcart-wrap-freegift') || '';
    var excluded = (zone.getAttribute('data-mcart-wrap-excluded') || '').split(',').filter(Boolean);
    return lastCart.items.reduce(function (acc, it) {
      if (it.handle === wrapHandle || (giftHandle && it.handle === giftHandle) || (it.properties && it.properties._mes_gift)) return acc;
      if (excluded.indexOf(it.handle) > -1) return acc;
      return acc + it.quantity;
    }, 0);
  }

  // PRODUCT MODE ONLY: the number of units the shopper actually ASKED to wrap — the summed
  // quantity of the cart lines whose per-line `gift-wrap-checkbox-<variantId>` is ticked.
  //
  // This is a different question from wrappableCount(), which answers "how many units COULD be
  // wrapped" and is the right notion for the auto-hide / orphan rules only. Using wrappableCount
  // as the product-mode TARGET charged for the entire cart the moment a single box was ticked
  // (measured on the dev base 27 Jul 2026: 2 bracelets, ONE box ticked -> wrap qty 2 = Rs 78 for
  // one wrapped product, and a stale qty 2 left behind after the other product was removed).
  // The checkbox is the truth where a row is rendered; the cookie backs rows not currently in
  // the DOM. Both are per-variant, matching how the legacy per-line handler writes them.
  function selectedWrapUnits(zone) {
    if (!lastCart) return 0;
    var wrapHandle = zone.getAttribute('data-mcart-wrap-handle') || '';
    var giftHandle = zone.getAttribute('data-mcart-wrap-freegift') || '';
    var excluded = (zone.getAttribute('data-mcart-wrap-excluded') || '').split(',').filter(Boolean);
    return lastCart.items.reduce(function (acc, it) {
      if (it.handle === wrapHandle || (giftHandle && it.handle === giftHandle) || (it.properties && it.properties._mes_gift)) return acc;
      if (excluded.indexOf(it.handle) > -1) return acc;
      var id = 'gift-wrap-checkbox-' + it.variant_id;
      var box = document.getElementById(id);
      // A box that exists but is HIDDEN is not an intent surface (round 6, 28 Jul 2026): the
      // drawer renders the per-line boxes invisibly (offsetParent null, measured 27 Jul), and
      // their checked state mirrors the CART — counting them made the wrap self-justifying:
      // zone off, but "selected" units > 0 because the hidden boxes echo the very line being
      // questioned, so a correction to zero could never fire. Visible box = truth; hidden
      // box = ignored; no box at all = the cookie backs it.
      var on = box ? (box.offsetParent !== null && box.checked) : document.cookie.indexOf(id + '=true') > -1;
      return on ? acc + it.quantity : acc;
    }, 0);
  }

  // Deferred product-mode wrap re-level — see the call site in syncWrapZone for why it waits.
  var wrapLevelTimer = null;
  var wrapLevelTries = 0;
  function relevelWrap() {
    var zone = drawer.querySelector('[data-mcart-wrapzone]');
    if (!zone || wrapPending !== null) return;
    if (zone.getAttribute('data-mcart-wrap-mode') !== 'product') return;
    var handle = zone.getAttribute('data-mcart-wrap-handle');
    // FRESH READ AT FIRE TIME (TA-41958 round 4, observed flake): Shopify re-hashes a line's
    // key on every write, so a key taken from `lastCart` can be one write stale — change.js
    // then 4xxs silently and the correction never lands (one removal run kept charging 3
    // wraps for 2 products; the identical rerun passed). Resolving the line from a fresh
    // cart read kills the stale-key class outright. One extra read per correction; the
    // correction path is rare.
    /* force=true: this is the correction path and it must see the cart AFTER the write it is
       checking, so it is the one caller that bypasses the freshness window. */
    fetchCart(true).then(function (c) {
      if (!c || wrapPending !== null) return;
      var line = null;
      for (var i = 0; i < c.items.length; i++) {
        if (c.items[i].handle === handle) { line = c.items[i]; break; }
      }
      var target = wrapTargetUnits(zone);   // re-read at fire time; zone-on = every wrappable unit
      // target 0 is a VALID target (round 6, 28 Jul 2026): zone settled-off with a wrap line
      // still in the cart (an untick write lost to a challenge, or the legacy machinery
      // re-adding a wrap after the zone was turned off) must converge to REMOVING the line —
      // the old `target <= 0` bail made that state uncorrectable, so the shopper stayed
      // silently charged Rs 39 with the toggle showing off.
      if (!line || line.quantity === target) return;
      wrapLevelTries++;
      var rootUrl = (window.Shopify && Shopify.routes) ? Shopify.routes.root : '/';
      var secId = drawerSectionId();
      fetch(rootUrl + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // by LINE KEY, so the gift-wrap line's per-SKU properties survive the quantity change.
        //
        // `sections` bundles the drawer's re-render INTO this same response so the correction
        // lands via the SOFT path (reconcile in place). Round 4 dispatched cart:refresh here
        // instead — which rebuilt the entire drawer on every wrap correction, i.e. on
        // essentially every tick. That is what made the thumbnails flash and the cart freeze:
        // the arithmetic fix was right, but it was paid for with a full teardown each time
        // (round 5, 28 Jul 2026). _onCartRefresh is now safe too, but the soft path is still
        // correct here — it is one request instead of two.
        body: JSON.stringify({ id: line.key, quantity: target, sections: secId })
      }).then(function (r) {
        // a failed write (rotated key, transient error) must RETRY, not vanish — bounded by tries
        if (!r.ok) {
          if (wrapLevelTries < 3) {
            clearTimeout(wrapLevelTimer);
            wrapLevelTimer = setTimeout(relevelWrap, 900);
          }
          return;   // round 4 fell through to a full refresh here on the 3rd failure
        }
        return r.json().then(function (j) { return softCartChange(j.sections); });
      }).catch(function () {
        if (wrapLevelTries < 3) {
          clearTimeout(wrapLevelTimer);
          wrapLevelTimer = setTimeout(relevelWrap, 900);
        }
      });
    });
  }

  // WHICH TARGET APPLIES (TA-41958 round 4 — decided by what the shopper can actually see).
  // The per-line wrap checkboxes are rendered but HIDDEN on the drawer (measured 27 Jul:
  // offsetParent null), so the ZONE toggle is the only wrap control a shopper touches — and
  // its meaning is "wrap EVERYTHING", printed right on it ("Rs 39 / item"). So when the zone
  // is on (or its intent is latched), the correct wrap quantity is every wrappable unit in
  // the cart — including items added AFTER the tick, which is exactly the case the wrap-all
  // machinery misses (it reads the cart once, at tick time; an item added moments before can
  // be missed by that read entirely — the read-modify-write race on camera in Priyanka's
  // recording). Only when the zone is off does per-line intent (visible boxes / cookies)
  // apply, e.g. surfaces that show the boxes.
  function wrapTargetUnits(zone) {
    var t = zone.querySelector('[data-mcart-wrap-toggle]');
    var zoneOn = wrapPending === true || (t && t.checked) || zone.classList.contains('is-on');
    return zoneOn ? wrappableCount(zone) : selectedWrapUnits(zone);
  }

  var orphanWrapCleaned = false;
  var orphanCardCleaned = false;   // same one-shot guard, for the greeting card (20 Aug 2026)
  function syncWrapZone() {
    var zone = drawer.querySelector('[data-mcart-wrapzone]');
    if (!zone || !lastCart) return;
    var handle = zone.getAttribute('data-mcart-wrap-handle');
    var inCart = lastCart.items.some(function (it) { return it.handle === handle; });
    // While an order-mode toggle is in flight, the user's intent wins over any stale cart read.
    // The real cart lags update.js by ~700ms (the cart:refresh fetch), and a server re-render or
    // an interim sync in that window read the OLD cart and flipped the checkbox back — so on
    // untick it went off -> popped back on -> off (Priyanka, 22 Jul). Hold the intent until a
    // fresh cart actually matches it, then release.
    if (wrapPending !== null) {
      // EXPIRY (TA-41958 round 4): if the latched intent is never confirmed by the cart —
      // the write was lost to a challenged/failed request — the latch used to hold the
      // checkbox in a state the cart never reached, indefinitely. That is the "ticked but
      // no charge" still-frame in Priyanka's recording. After 8s of non-confirmation the
      // cart is the truth again and the checkbox reverts honestly.
      if (inCart === wrapPending) { wrapPending = null; }
      else if (Date.now() - wrapPendingAt > 8000) {
        console.warn('[mcart] wrap intent never confirmed by the cart — reverting to cart truth');
        wrapPending = null;
      }
      else { inCart = wrapPending; }
    }
    zone.classList.toggle('is-on', inCart);
    var check = zone.querySelector('[data-mcart-wrap-toggle]');
    // GUARD NARROWED (TA-41958 round 6, 28 Jul 2026). This used to skip the correction
    // whenever the checkbox was document.activeElement — meant as "don't fight the user
    // mid-interaction". But on a phone a tapped checkbox KEEPS focus indefinitely (nothing
    // else ever takes it), so after a lost untick-write the box could never be corrected
    // back to cart truth: it sat unchecked while the cart kept charging the wrap — the
    // exact "unchecked but active" state in Shrey's 28 Jul screenshot (Rs 1,238 = item +
    // Rs 39 wrap, box unticked). The wrapPending latch above already protects the genuine
    // in-flight window; the only moment left to protect is an actual mid-press, so that is
    // now the only moment skipped.
    if (check && !check.matches(':active')) check.checked = inCart;
    var note = zone.querySelector('[data-mcart-notecard]');
    if (note) note.classList.toggle('is-open', inCart);

    // Auto-hide + disable when nothing in the cart can be wrapped (Shrey, 21 Jul 2026).
    // The zone is outside the swap regions, so JS owns this state between re-renders.
    var wrappable = wrappableCount(zone);
    zone.hidden = wrappable === 0;
    if (check) check.disabled = wrappable === 0;

    // Order-level mode: a wrap item with nothing left to wrap is an orphan — remove it so
    // the customer isn't silently charged for wrapping an empty cart. One-shot per state.
    if (zone.getAttribute('data-mcart-wrap-mode') === 'order' && wrappable === 0 && inCart && !orphanWrapCleaned) {
      orphanWrapCleaned = true;
      orderWrapSet(false, zone.getAttribute('data-mcart-wrap-variant'));
      clearGiftNote(); // an orphaned wrap's note is orphaned with it
    }
    if (wrappable > 0) orphanWrapCleaned = false;

    // An orphaned CARD is worse than an orphaned wrap, because the card line is hidden from the
    // list (20 Aug 2026): measured after that change shipped, removing the last real product left
    // a Rs 49 card in the cart with the badge reading 0 and nothing visible to remove -- an
    // invisible charge. Runs in BOTH wrap modes, unlike the wrap rule above, because the card is
    // not mode-dependent. One-shot per state, same as the wrap.
    if (wrappable === 0 && !orphanCardCleaned) {
      var hasCard = !!(lastCart && lastCart.items && lastCart.items.some(function (it) {
        return knownCardVariantIds().indexOf(Number(it.variant_id)) !== -1;
      }));
      if (hasCard) { orphanCardCleaned = true; clearGiftCard(); }
    }
    if (wrappable > 0) orphanCardCleaned = false;

    // PRODUCT MODE: keep the wrap QUANTITY level with the cart (TA-41958, Priyanka 27 Jul 2026 —
    // "the gift wrap checkbox state, cart line item and pricing are not staying in sync").
    // The wrap-all control only recalculates when it is USED, so wrapping 2 items and then adding
    // a third left the wrap at 2: two wraps for three products, and a total the shopper cannot
    // explain. Measured before this: wrappable 3, wrap qty 2.
    // Loop safety, since this runs inside the cart:change / cart:refresh path and writes to the
    // cart: (a) nothing while a toggle is in flight (wrapPending), so the user always wins;
    // (b) `wrapQtySynced` records the quantity we last wrote and blocks a repeat for the same
    // target, so the write -> refresh -> sync cycle settles after exactly one pass;
    // (c) we change the LINE by key rather than by variant, which preserves the gift-wrap line
    // properties, and never touch a cart with nothing wrappable (the orphan rule above owns that).
    if (mode() === 'product' && wrapPending === null && inCart && wrappable > 0) {
      var wrapLine = null;
      for (var i = 0; i < lastCart.items.length; i++) {
        if (lastCart.items[i].handle === handle) { wrapLine = lastCart.items[i]; break; }
      }
      // Target per the visible intent surface: zone-on = every wrappable unit; else per-line ticks.
      var target = wrapTargetUnits(zone);
      // DEBOUNCED, AND DELIBERATELY LAST (TA-41958, measured 27 Jul 2026). Two independent systems
      // write this one line: this file and the legacy per-line machinery in theme.js.liquid. On a
      // removal the observed order was: remove the product -> WE correctly set wrap to 1 -> legacy
      // immediately put it back to 2 (its `_addProductToCart` path re-adds a wrap for the surviving
      // product, reached from a stale localStorage `variantPickerId`), so an instant write always
      // lost the race and the shopper kept paying for two wraps with one product in the cart.
      // Correcting on a settle delay instead means the legacy writes land first and ours is the
      // last word. Bounded to 3 attempts per target so a wrap line we genuinely cannot move can
      // never become a write loop, and the target is RE-READ at fire time so a user action during
      // the delay wins over the value we queued.
      if (wrapLine && target > 0 && wrapLine.quantity !== target) {
        if (wrapQtySynced !== target) { wrapQtySynced = target; wrapLevelTries = 0; }
        if (wrapLevelTries < 3) {
          clearTimeout(wrapLevelTimer);
          wrapLevelTimer = setTimeout(relevelWrap, 900);
        }
      } else if (wrapLine && wrapLine.quantity === target) {
        wrapQtySynced = null;               // in step; re-arm for the next genuine drift
        wrapLevelTries = 0;
        clearTimeout(wrapLevelTimer);
      }
    }
  }

  function mode() {
    var z = drawer.querySelector('[data-mcart-wrapzone]');
    return z ? z.getAttribute('data-mcart-wrap-mode') : '';
  }

  // Order-level gift wrap: set the wrap variant's absolute quantity (1 or 0) directly —
  // /cart/update.js adds the item when missing and removes at 0, so it's idempotent even
  // if an earlier product-level state left qty > 1 (Shrey, 21 Jul 2026).
  //
  // DEBOUNCED (Shrey, 24 Jul 2026 — rapid tick/untick "second click doesn't register"): the
  // network write + its cart:refresh re-render replaces the checkbox node, so a tap that lands
  // mid-flight was lost. The checkbox VISUAL updates instantly (the change handler already flips
  // .is-on / .is-open / checked), and we only fire ONE update.js for the LATEST intent after the
  // taps settle — so rapid on/off/on collapses to a single write to the final state, nothing is
  // dropped, and no re-render happens until the user stops.
  var wrapNetTimer = null;
  var wrapNetVariant = null;
  // Unchecking the wrap must also clear the gift note (Shrey, 17 Aug 2026). The drawer's
  // note card IS the cart note (cart.note), so before this, untick removed the wrap line
  // and slid the card closed — but the note stayed saved on the cart, and dispatch kept
  // receiving gift notes on orders with no gift wrap. Two halves, both needed:
  //   - the server: POST note:'' (cart.note is the only place the note persists)
  //   - the DOM: empty the textarea WITHOUT firing change — the cart-note component saves
  //     on change, and a second in-flight save here would race the one below. Emptying the
  //     DOM matters because the card only hides on untick; re-ticking would otherwise show
  //     the old text as if it were still saved.
  function clearGiftNote() {
    drawer.querySelectorAll('[data-mcart-notecard] textarea').forEach(function (t) { t.value = ''; });
    var root = (window.Shopify && Shopify.routes) ? Shopify.routes.root : '/';
    fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: '' })
    }).catch(function () { /* the note is cosmetic state; never break the wrap toggle over it */ });
  }

  function orderWrapSet(on, variantId) {
    if (!variantId) return;
    // Latch the intent so intermediate syncs / a stale server re-render can't revert the
    // checkbox before the real cart catches up (flicker fix, Priyanka 22 Jul).
    wrapPending = !!on;
    wrapPendingAt = Date.now();
    wrapNetVariant = variantId;
    clearTimeout(wrapNetTimer);
    wrapNetTimer = setTimeout(function () {
      var target = wrapPending;                 // the final settled intent
      var root = (window.Shopify && Shopify.routes) ? Shopify.routes.root : '/';
      var updates = {};
      updates[wrapNetVariant] = target ? 1 : 0;
      var secId = drawerSectionId();
      fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates, sections: secId })
      }).then(function (r) { return r.json(); }).then(function (j) {
        // surgical: reconcile via cart:change with the bundled drawer render — the header,
        // quick-add row and wrap zone keep their DOM nodes (no full-drawer flash)
        return softCartChange(j.sections);
      }).catch(function () {
        hardRefresh();
      });
    }, 320);
  }

  /* ---------------- gift progress labels ---------------- */

  // The two labels under the progress bar ("Free shipping" / the gift goal amount) were
  // Liquid-only, so a re-render carrying a stale cart snapshot could leave a ✓ on an
  // unreached goal (observed 24 Jul 2026: "✓ ₹3,000" while the message said "Spend ₹742
  // more"). Drive them from the SAME live source the message/marker use — the
  // free-shipping-bar element's own attributes — so they can never disagree again.
  function syncProgressLabels() {
    var bar = drawer.querySelector('free-shipping-bar.mcart-progress');
    if (!bar) return;
    var total = parseFloat(bar.getAttribute('total-price'));
    if (isNaN(total)) return;
    var shipThr = parseFloat(bar.getAttribute('threshold'));
    var giftThr = parseFloat(bar.getAttribute('free-gift-threshold'));
    // ⚠ WRITE-GUARDED: this runs inside the MutationObserver, and setting textContent
    // ALWAYS replaces the text node (even with an identical string), which re-fires the
    // observer -> infinite loop -> page freeze (shipped broken in V9.21.36, caught same
    // day). Only write when the value actually changes so steady state is mutation-free.
    var ship = bar.querySelector('.mcart-progress__ship');
    if (ship && !isNaN(shipThr)) {
      var shipHit = total >= shipThr;
      var shipText = (shipHit ? '✓ ' : '') + 'Free shipping';
      if (ship.classList.contains('mcart-progress__ship--pending') === shipHit) {
        ship.classList.toggle('mcart-progress__ship--pending', !shipHit);
      }
      if (ship.textContent !== shipText) ship.textContent = shipText;
    }
    var goal = bar.querySelector('.mcart-progress__goal');
    if (goal && !isNaN(giftThr)) {
      var goalHit = total >= giftThr;
      var goalText = (goalHit ? '✓ ' : '') + fmtMoney(giftThr);
      if (goal.classList.contains('mcart-progress__goal--hit') !== goalHit) {
        goal.classList.toggle('mcart-progress__goal--hit', goalHit);
      }
      if (goal.textContent !== goalText) goal.textContent = goalText;
    }
  }

  /* ---------------- quick-add re-render cache ---------------- */

  // Every drawer re-render replaces the <product-recommendations> node; the fresh element
  // arrives EMPTY (the section-render endpoint never has recommendations.performed) and
  // re-fetches, so the dynamic cards (3rd onward) vanished + popped back on every qty
  // change (Shrey, 24 Jul 2026). Cache the last rendered markup per product|limit key and
  // refill an empty element instantly; the element's own fetch then lands identical markup
  // with no visible change. Also restore the row's horizontal scroll position, which the
  // re-render resets to 0.
  var recCache = {};
  var addonsScrollLeft = 0;
  function syncRecCache() {
    drawer.querySelectorAll('product-recommendations').forEach(function (el) {
      var key = (el.getAttribute('product') || '') + '|' + (el.getAttribute('limit') || '');
      if (el.childElementCount > 0) recCache[key] = el.innerHTML;
      else if (recCache[key]) el.innerHTML = recCache[key];
    });
    var sc = drawer.querySelector('.mcart-addons__scroll');
    if (sc && sc.scrollLeft === 0 && addonsScrollLeft > 0) sc.scrollLeft = addonsScrollLeft;
  }
  // scroll doesn't bubble — capture-listen on the drawer and remember the row's position
  drawer.addEventListener('scroll', function (e) {
    var t = e.target;
    if (t && t.classList && t.classList.contains('mcart-addons__scroll')) addonsScrollLeft = t.scrollLeft;
  }, true);

  /* ---------------- quick-buy inert-script re-activation ---------------- */

  // The quick-buy drawer inserts fetched content parsed by DOMParser, whose <script> tags
  // never execute — the V3 size guide self-wires via document.currentScript, so it was dead
  // (fully expanded, no flip) in cart quick-adds outside the healing collection. Same binder
  // the healing section uses, behind the SAME global flag so exactly one binds document-wide
  // (Shrey, 24 Jul 2026).
  if (!window._mhcQbSgWire) {
    window._mhcQbSgWire = true;
    document.addEventListener('dialog:after-show', function (e) {
      var qb = e.target;
      if (!qb.matches || !qb.matches('quick-buy-drawer.mhc-qb') || qb._mhcSgWired) return;
      var inert = qb.querySelectorAll('script');
      if (!inert.length) return;
      qb._mhcSgWired = true;
      inert.forEach(function (old) {
        /* V9.28.81 (H374): keep the script's own type. Re-creating a <script type="module"> as a classic script
           threw "Cannot use import statement outside a module" (Safari: "Unexpected token '*'") on every Quick Add
           that shows the personalisation box (snippets/input.liquid, since V9.28.39), and ran JSON blocks as code. */
        var type = (old.getAttribute('type') || '').toLowerCase();
        if (type && type !== 'module' && type !== 'text/javascript' && type !== 'application/javascript') return;
        var live = document.createElement('script');
        for (var i = 0; i < old.attributes.length; i++) live.setAttribute(old.attributes[i].name, old.attributes[i].value);
        live.textContent = old.textContent;
        old.parentNode.replaceChild(live, old);
      });
    });
  }

  /* ---------------- undo toast ---------------- */

  function stripEl() { return drawer.querySelector('[data-mcart-strip]'); }

  function clearToast() {
    clearTimeout(toastTimer);
    activeToast = null;
    var strip = stripEl();
    if (!strip) return;
    strip.classList.remove('is-toast');
    var toast = strip.querySelector('.mcart-strip__toast');
    var undo = strip.querySelector('.mcart-strip__undo');
    if (toast) toast.remove();
    if (undo) undo.remove();
    // the strip stays visible when it carries the save message OR the offer nudge
    if (!strip.querySelector('.mcart-strip__save') && !strip.querySelector('.mcart-strip__nudge')) strip.hidden = true;
  }

  // paints activeToast onto whatever strip currently exists (re-renders replace the DOM,
  // so this is re-applied from the sync passes until the toast expires)
  function renderToast() {
    if (!activeToast) return;
    if (Date.now() > activeToast.expiresAt) { pendingRemove = null; clearToast(); return; }
    var strip = stripEl();
    if (!strip) return;
    if (!strip.querySelector('.mcart-strip__toast')) {
      var msg = document.createElement('span');
      msg.className = 'mcart-strip__toast';
      msg.textContent = activeToast.message;
      var undo = document.createElement('button');
      undo.type = 'button';
      undo.className = 'mcart-strip__undo';
      undo.setAttribute('data-mcart-undo', '');
      undo.textContent = 'Undo';
      strip.appendChild(msg);
      strip.appendChild(undo);
    }
    strip.hidden = false;
    strip.classList.add('is-toast');
  }

  function showToast(message) {
    clearTimeout(toastTimer);
    activeToast = { message: message, expiresAt: Date.now() + 6000 };
    renderToast();
    toastTimer = setTimeout(function () {
      pendingRemove = null;
      clearToast();
    }, 6000);
  }

  function captureRemoveIntent(lineKey) {
    if (!lastCart || !lineKey) return;
    var item = null;
    for (var i = 0; i < lastCart.items.length; i++) {
      if (lastCart.items[i].key === lineKey) { item = lastCart.items[i]; break; }
    }
    if (!item) return;
    var skip = specialHandles();
    if (skip.indexOf(item.handle) > -1) return; // wrap/free-gift lines: no undo
    pendingRemove = {
      key: lineKey,
      title: item.product_title || item.title,
      prevSavings: cartSavings(lastCart),
      item: {
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties || {},
        selling_plan: item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : undefined
      }
    };
  }

  function undoRemove() {
    if (!pendingRemove) return;
    var payload = { items: [pendingRemove.item], sections: withCartJson(drawerSectionId()) };
    pendingRemove = null;
    clearToast();
    fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) { if (window.MesCartJson) window.MesCartJson.invalidate(); return r.json(); }).then(function (j) {
      return softCartChange(j.sections);   // reconcile in place — the restored row is inserted, nothing else flashes
    }).catch(function () { /* leave cart as is */ });
  }

  function maybeToastAfterChange(newCart) {
    if (!pendingRemove) return;
    var stillThere = newCart.items.some(function (it) { return it.key === pendingRemove.key; });
    if (stillThere) { pendingRemove = null; return; }
    var lostDiscount = pendingRemove.prevSavings > 0 && cartSavings(newCart) <= 0;
    var pct = Math.round(rate() * 100);
    var message;
    if (lostDiscount) {
      /* The offer-loss line is customizer copy (Shrey, 14 Aug 2026). {pct} is
         substituted with the live discount percentage, so the sentence follows
         tier_rate and never has to be edited when the offer changes.
         BLANK SETTING = NO TOAST. Some campaigns do not want to tell a shopper
         what they just gave up, so an empty field suppresses the message
         entirely rather than falling back to a hardcoded default. The plain
         "removed" toast is unaffected — that one is feedback, not persuasion. */
      var tpl = cfg('data-mcart-lost-text', '');
      /* no copy set, or no known rate to put in it -> say nothing */
      if (!tpl || !pct) { pendingRemove = null; return; }
      message = tpl.replace(/\{pct\}/g, pct);
    } else {
      message = '"' + pendingRemove.title + '" removed';
    }
    // the line-items region re-renders ~1.25s after the change; show the toast after it settles
    setTimeout(function () { showToast(message); }, 1400);
  }

  /* ---------------- pill switcher: Suggested <-> Recently viewed ---------------- */
  // The collapsible RV dropdown became the second tab of a pill switcher on the quick-add
  // row (Shrey, 24 Jul 2026): the product row below swaps IN PLACE, thumb slides via CSS
  // transform. Tab choice + the fetched RV markup survive server re-renders (module state
  // re-applied in the sync passes / MutationObserver).

  var activeTab = 'suggested';
  var rvCache = '';

  function recentIds() {
    try {
      var raw = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
      if (!Array.isArray(raw)) return [];
      var inCart = lastCart ? lastCart.items.map(function (it) { return it.product_id; }) : [];
      return raw.filter(function (id) { return inCart.indexOf(id) === -1; }).slice(0, 4);
    } catch (e) { return []; }
  }

  function setTab(tab) {
    activeTab = tab;
    var sw = drawer.querySelector('[data-mcart-swtch]');
    if (!sw) return;
    sw.setAttribute('data-mcart-active', tab);
    sw.querySelectorAll('[data-mcart-swtch-opt]').forEach(function (b) {
      var on = b.getAttribute('data-mcart-swtch-opt') === tab;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', String(on));
    });
    var sug = drawer.querySelector('[data-mcart-pane="suggested"]');
    var rec = drawer.querySelector('[data-mcart-pane="recent"]');
    if (sug) sug.hidden = tab !== 'suggested';
    if (rec) rec.hidden = tab !== 'recent';
    if (tab === 'recent' && !recentLoaded) loadRecent();
    // each pane starts from its beginning; also drop the stored scroll so the
    // rebuild-restore in syncRecCache can't yank it back
    var sc = drawer.querySelector('.mcart-addons__scroll');
    if (sc) sc.scrollLeft = 0;
    addonsScrollLeft = 0;
  }

  // one skeleton card (matches .mcart-upcard footprint) so the recent pane holds its height
  // while the products fetch — no layout shift below (Shrey, 24 Jul 2026).
  function skeletonCards(n) {
    var one = '<div class="mcart-upcard mcart-upcard--skel" aria-hidden="true">'
      + '<div class="mcart-upcard__media mcart-skel-shine"></div>'
      + '<div class="mcart-upcard__body">'
      + '<span class="mcart-skel-line mcart-skel-shine" style="width:90%"></span>'
      + '<span class="mcart-skel-line mcart-skel-shine" style="width:60%"></span>'
      + '<div class="mcart-upcard__row"><span class="mcart-skel-line mcart-skel-shine" style="width:45%;height:16px"></span></div>'
      + '<span class="mcart-skel-btn mcart-skel-shine"></span>'
      + '</div></div>';
    var out = '';
    for (var i = 0; i < n; i++) out += one;
    return out;
  }

  function loadRecent() {
    var list = drawer.querySelector('[data-mcart-recent-list]');
    if (!list || recentLoaded) return Promise.resolve();
    var ids = recentIds();
    if (!ids.length) return Promise.resolve();
    recentLoaded = true;
    // paint skeletons immediately (synchronous) so the pane has its full height before the
    // fetch resolves; replaced wholesale by the real cards on load.
    list.innerHTML = skeletonCards(ids.length);
    var q = ids.map(function (id) { return 'id:' + id; }).join(' OR ');
    var root = (window.Shopify && Shopify.routes ? Shopify.routes.root : '/');
    var url = root + 'search?type=product&q=' + encodeURIComponent(q) + '&section_id=mes-cart-recent';
    return fetch(url).then(function (r) { return r.text(); }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var src = doc.querySelector('[data-mcart-recent-src]');
      if (!src || !src.children.length) { list.innerHTML = ''; return; }
      list.replaceChildren.apply(list, Array.prototype.slice.call(document.importNode(src, true).childNodes));
      rvCache = list.innerHTML;
      syncEffPanels();
    }).catch(function () { list.innerHTML = ''; recentLoaded = false; });
  }

  // Show the switcher only when the visitor has recently-viewed products, and re-assert
  // the active tab + refill the RV pane after a server re-render (which always comes back
  // as "suggested visible / recent empty+hidden").
  function syncSwitcher() {
    var sw = drawer.querySelector('[data-mcart-swtch]');
    if (!sw) return;
    var has = recentIds().length > 0;
    sw.hidden = !has;
    if (!has && activeTab === 'recent') activeTab = 'suggested';
    var rec = drawer.querySelector('[data-mcart-pane="recent"]');
    if (rec && !rec.children.length) {
      recentLoaded = false;
      if (rvCache) { rec.innerHTML = rvCache; recentLoaded = true; }
    }
    var sug = drawer.querySelector('[data-mcart-pane="suggested"]');
    sw.setAttribute('data-mcart-active', activeTab);
    sw.querySelectorAll('[data-mcart-swtch-opt]').forEach(function (b) {
      var on = b.getAttribute('data-mcart-swtch-opt') === activeTab;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', String(on));
    });
    if (sug) sug.hidden = activeTab !== 'suggested';
    if (rec) rec.hidden = activeTab !== 'recent';
  }

  /* ---------------- gift note helpers ---------------- */

  function giftTextarea() {
    var areas = document.querySelectorAll('textarea[name="note"]');
    for (var i = 0; i < areas.length; i++) {
      if (areas[i].id && areas[i].id.indexOf('gift') > -1) return areas[i];
    }
    return null;
  }

  // the server renders the RAW cart note ("Gift note: ..." / "Request: ..." lines) into the
  // inline textarea; the old dialog's splitter is gone, so strip it down to the gift text here
  function sanitizeGiftNote() {
    var area = giftTextarea();
    if (!area || area === document.activeElement) return;
    var v = area.value || '';
    if (v.indexOf('Gift note:') === -1 && v.indexOf('Request:') === -1) return;
    var gift = [];
    v.split('\n').forEach(function (line) {
      if (line.indexOf('Gift note:') > -1) {
        var t = line.split('Gift note:')[1];
        if (t && t.trim()) gift.push(t.trim());
      } else if (line.indexOf('Request:') > -1) {
        // order-note remnant: not editable in the drawer anymore, keep it out of the gift field
      } else if (line.trim()) {
        gift.push(line.trim());
      }
    });
    area.value = gift.join('\n');
  }

  function focusGiftNote() {
    var zone = drawer.querySelector('[data-mcart-wrapzone]');
    var note = zone ? zone.querySelector('[data-mcart-notecard]') : null;
    if (note) {
      note.classList.add('is-open');
      note.scrollIntoView({ block: 'center', behavior: 'smooth' });
      var area = giftTextarea();
      if (area) setTimeout(function () { area.focus(); }, 250);
    }
  }

  /* ---------------- swipe-down-to-close: REMOVED (Shrey, 20 Jul 2026) ----------------
     The drag-to-dismiss gesture fought the drawer's own WAAPI open/close animation (the sheet is a
     shadow `part=content`, animated in JS), which made the sheet snap back to the top on release and
     left animation state that broke the next open (disappearing X / changing height). Shrey's call:
     remove the gesture entirely rather than keep fighting it. The cart closes via the round close
     button (visible on mobile) and tapping the dim overlay — both native and unaffected. The grabber
     pill in the header is left as a purely visual bottom-sheet affordance. */

  /* ---------------- greeting card picker (Shrey, 20 Aug 2026) ----------------
     ONE card per order, mirroring the gift note which is also per-order.
     The free "brown paper" default is the ABSENCE of a card line, never a ₹0 product, so an
     untouched picker adds nothing to the order and dispatch sees no filler line.
     Selecting swaps: remove whichever card line is present, add the chosen one. */
  function cardEls() { return drawer.querySelectorAll('[data-mcart-card]'); }

  function knownCardVariantIds() {
    var ids = [];
    cardEls().forEach(function (b) {
      var v = b.getAttribute('data-mcart-card-variant');
      if (v) { ids.push(Number(v)); }
    });
    return ids;
  }

  /* Reflect the CART (not the click) back into the tiles, so the picker cannot drift from
     reality — a card removed from the cart rows must un-select its tile. */
  function paintCardSelection(cart) {
    var known = knownCardVariantIds();
    var chosen = null;
    (cart && cart.items ? cart.items : []).forEach(function (it) {
      if (known.indexOf(Number(it.variant_id)) !== -1) { chosen = Number(it.variant_id); }
    });
    cardEls().forEach(function (b) {
      var v = b.getAttribute('data-mcart-card-variant');
      var on = chosen === null ? !v : Number(v) === chosen;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function chooseCard(btn) {
    if (cardBusy) { return; }
    cardBusy = true;
    /* Acknowledge the tap immediately (Shrey, 20 Aug 2026). The swap is two or three network
       round trips, and with no feedback the tile looks inert, so people tap again -- which the
       cardBusy guard silently swallows, making it look doubly broken. The row is marked busy as
       a whole (pointer-events off) rather than only the tapped tile, because the failure we are
       preventing is a SECOND tap landing on a DIFFERENT card. */
    var cardsRoot = drawer.querySelector('[data-mcart-cards]');
    if (cardsRoot) { cardsRoot.classList.add('is-busy'); }
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    var wanted = btn.getAttribute('data-mcart-card-variant');
    var known = knownCardVariantIds();
    var root_ = (window.Shopify && Shopify.routes) ? Shopify.routes.root : '/';
    fetch(root_ + 'cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      var updates = {};
      (cart.items || []).forEach(function (it) {
        if (known.indexOf(Number(it.variant_id)) !== -1) { updates[it.variant_id] = 0; }
      });
      var removeFirst = Object.keys(updates).length
        ? fetch(root_ + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: updates }) })
        : Promise.resolve();
      return removeFirst.then(function () {
        if (!wanted) { return fetch(root_ + 'cart.js').then(function (r) { return r.json(); }); }
        return fetch(root_ + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(wanted), quantity: 1 }], sections: drawerSectionId() })
        }).then(function () { return fetch(root_ + 'cart.js').then(function (r) { return r.json(); }); });
      });
    }).then(function (cart) {
      paintCardSelection(cart);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }).catch(function () { /* leave the cart as it is; the next paint re-syncs from it */ })
      .then(function () {
        cardBusy = false;
        /* Cleared in the settle handler, not the success one: a failed swap that leaves the row
           frozen is worse than the missing spinner was. */
        if (cardsRoot) { cardsRoot.classList.remove('is-busy'); }
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      });
  }

  /* Unticking the wrap must also drop the chosen card. The card is a CHILD of gift wrap
     (Shrey, 20 Aug 2026: brown paper is "included with gift wrap"), and the picker collapses
     out of view with the note panel — so a card left behind bills the shopper for something
     they can no longer see or change from the surface they chose it on. Mirrors
     clearGiftNote(). Safe to fire alongside the wrap removal: cart/update.js touches only the
     variants it names, and chooseCard() re-reads the cart at fire time rather than trusting a
     snapshot. Measured 20 Aug before this existed: untick left "Thank You Greeting Card" x1
     (Rs 49) in a cart with no wrap line. */
  function clearGiftCard(tries) {
    var plain = null;
    cardEls().forEach(function (b) { if (!b.getAttribute('data-mcart-card-variant')) { plain = b; } });
    if (!plain) { return; }
    /* A pick still in flight would make chooseCard() no-op and lose the clear silently, so
       wait for it instead of dropping it (bounded, ~3s). */
    if (cardBusy) {
      if ((tries || 0) < 10) { setTimeout(function () { clearGiftCard((tries || 0) + 1); }, 300); }
      return;
    }
    chooseCard(plain);
  }

  /* Preset -> card filter. Two deliberate degradations, both so the row never lies:
     no group set OR nothing matches -> show EVERY card;
     the currently selected card always stays visible, so a choice never vanishes mid-edit. */
  function filterCards(group) {
    var cards = cardEls();
    if (!cards.length) { return; }
    var matches = 0;
    if (group) {
      cards.forEach(function (b) {
        var v = b.getAttribute('data-mcart-card-variant');
        if (!v) { return; }
        var tags = (b.getAttribute('data-mcart-card-tags') || '').toLowerCase();
        if (tags.indexOf('card-occasion:' + group.toLowerCase()) !== -1) { matches++; }
      });
    }
    cards.forEach(function (b) {
      var v = b.getAttribute('data-mcart-card-variant');
      if (!v) { b.hidden = false; return; }              /* the free default is always offered */
      if (!group || matches === 0) { b.hidden = false; return; }
      var tags = (b.getAttribute('data-mcart-card-tags') || '').toLowerCase();
      var hit = tags.indexOf('card-occasion:' + group.toLowerCase()) !== -1;
      b.hidden = !hit && !b.classList.contains('is-selected');
    });
  }

  /* ---------------- event wiring ---------------- */

  // delegated clicks inside the drawer (survive every re-render)
  drawer.addEventListener('click', function (event) {
    var t = event.target;

    var undoBtn = t.closest ? t.closest('[data-mcart-undo]') : null;
    if (undoBtn) { event.preventDefault(); undoRemove(); return; }

    var cardBtn = t.closest ? t.closest('[data-mcart-card]') : null;
    if (cardBtn) { event.preventDefault(); chooseCard(cardBtn); return; }

    var noteChip = t.closest ? t.closest('[data-mcart-notechip]') : null;
    if (noteChip) { filterCards(noteChip.getAttribute('data-card-group')); }   /* falls through: the chip still fills the note */

    // single-variant quick-add: add directly, no popup (Shrey, 24 Jul 2026)
    var directAdd = t.closest ? t.closest('[data-mcart-direct-add]') : null;
    if (directAdd) {
      event.preventDefault();
      if (directAdd.hasAttribute('aria-busy')) return;
      var addLabel = directAdd.textContent;
      directAdd.setAttribute('aria-busy', 'true');
      directAdd.textContent = '…';
      var addRoot = (window.Shopify && Shopify.routes) ? Shopify.routes.root : '/';
      fetch(addRoot + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        /* Source tagging (Shrey, 19 Aug 2026): a hidden line item property records WHICH
           placement produced the sale, the same way Capes tags `_capes_source`. Underscore
           prefix = invisible to the shopper, permanent on the order line.
           The slug is read from the PANE the card sits in — "Suggested" and "Recently viewed"
           share this one button, so a fixed string would file every recently-viewed add under
           cart-suggested and quietly overstate the suggestion engine. */
        body: JSON.stringify({ items: [{ id: Number(directAdd.getAttribute('data-mcart-direct-add')), quantity: 1, properties: { _mes_source: (function () { var pane = directAdd.closest('[data-mes-source]'); return pane ? pane.getAttribute('data-mes-source') : 'cart-suggested'; })() } }], sections: withCartJson(drawerSectionId()) })
      }).then(function (r) {
        if (window.MesCartJson) window.MesCartJson.invalidate();
        /* V9.28.215 (buying-journey audit, Shrey 14 Oct): a refused add (sold out, stock limit = HTTP 422) used to drop the
           card as if it had been added and leave nothing in the cart. Treat it as a failure: the catch restores the button. */
        if (!r.ok) throw new Error('add ' + r.status);
        return r.json();
      }).then(function (j) {
        // the server only excludes in-cart products on a full re-render, which we no longer do —
        // drop this card client-side so the row stays truthful, then reconcile in place
        var deadCard = directAdd.closest('.mcart-upcard, .mcart-mancard');
        if (deadCard) deadCard.remove();
        return softCartChange(j.sections);
      }).catch(function () {
        directAdd.removeAttribute('aria-busy');
        directAdd.textContent = addLabel;
      });
      return;
    }

    var swtchOpt = t.closest ? t.closest('[data-mcart-swtch-opt]') : null;
    if (swtchOpt) { setTab(swtchOpt.getAttribute('data-mcart-swtch-opt')); return; }

    // yellow offer nudge -> scroll the body to the quick-add row (Shrey, 21 Jul 2026).
    // scrollIntoView on a slotted node does NOT reach the shadow [part="body"] scroller
    // (measured 21 Jul), so scroll the body part directly.
    var nudgeBtn = t.closest ? t.closest('[data-mcart-offnudge]') : null;
    if (nudgeBtn) {
      var quickAdd = drawer.querySelector('[data-mcart-addons]') || drawer.querySelector('[data-mcart-recent]');
      var bodyPart = drawer.shadowRoot && drawer.shadowRoot.querySelector('[part="body"]');
      if (quickAdd && bodyPart) {
        // rAF ease instead of scrollTo({behavior:'smooth'}) — the smooth variant silently
        // no-ops on this scroller (measured 21 Jul: call issued, position never moves;
        // plain scrollTop assignment works). easeOutCubic over 380ms, compositor-free.
        var from = bodyPart.scrollTop;
        var to = Math.max(0, quickAdd.getBoundingClientRect().top - bodyPart.getBoundingClientRect().top + from - 8);
        var t0 = null;
        var step = function (ts) {
          if (t0 === null) t0 = ts;
          var p = Math.min(1, (ts - t0) / 380);
          bodyPart.scrollTop = from + (to - from) * (1 - Math.pow(1 - p, 3));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
      return;
    }

    var noteChip = t.closest ? t.closest('[data-mcart-notechip]') : null;
    if (noteChip) {
      var area = giftTextarea();
      if (area) {
        area.value = noteChip.getAttribute('data-notes') || '';
        area.dispatchEvent(new Event('change', { bubbles: true }));
        area.dispatchEvent(new Event('input', { bubbles: true }));
      }
      return;
    }

    var noteFocus = t.closest ? t.closest('[data-mcart-notefocus]') : null;
    if (noteFocus) { focusGiftNote(); return; }

    // capture remove intent for the undo toast (before the change lands)
    var removeLink = t.closest ? t.closest('.mcart-li__x[href]') : null;
    if (removeLink) {
      var li = removeLink.closest('.mcart-li');
      if (li) captureRemoveIntent(li.getAttribute('data-mcart-key'));
      return;
    }
    var dec = t.closest ? t.closest('.quantity-decrease') : null;
    if (dec && dec.getAttribute('data-quantity') === '0') {
      var liDec = dec.closest('.mcart-li');
      if (liDec) captureRemoveIntent(liDec.getAttribute('data-mcart-key'));
    }
  });

  // gift wrap checkbox -> ORDER mode: one wrap qty set directly (update.js);
  //                       PRODUCT mode: the existing wrap-all machinery via the hidden proxies
  drawer.addEventListener('change', function (event) {
    // Per-line wrap box (legacy machinery owns the write): schedule a VERIFY pass. If the
    // write lands correctly the pass is a no-op (line qty == ticked units); if it was lost or
    // raced, relevelWrap corrects the line to what the shopper actually ticked. A user change
    // is genuine new intent, so the bounded-attempts counter re-arms. (TA-41958 round 3.)
    var lineBox = event.target.closest ? event.target.closest('[data-action="add-product-gift-wrap"]') : null;
    if (lineBox) {
      wrapQtySynced = null;
      wrapLevelTries = 0;
      clearTimeout(wrapLevelTimer);
      wrapLevelTimer = setTimeout(relevelWrap, 1600);
      return;
    }
    var check = event.target.closest ? event.target.closest('[data-mcart-wrap-toggle]') : null;
    if (!check) return;
    var zone = check.closest('[data-mcart-wrapzone]');
    if (!zone) return;
    var note = zone.querySelector('[data-mcart-notecard]');
    zone.classList.toggle('is-on', check.checked);
    if (note) note.classList.toggle('is-open', check.checked);
    // The untick clears the gift note in BOTH modes — placed here, above the mode branch,
    // because product mode exits through the legacy proxy click and order mode through
    // orderWrapSet, and this is the last line the two paths share.
    if (!check.checked) { clearGiftNote(); clearGiftCard(); }
    if (zone.getAttribute('data-mcart-wrap-mode') === 'order') {
      orderWrapSet(check.checked, zone.getAttribute('data-mcart-wrap-variant'));
      return;
    }
    // LATCH THE INTENT IN PRODUCT MODE TOO (TA-41958, Priyanka 27 Jul 2026: "it briefly appears
    // checked but then automatically unchecks itself").
    // The flicker fix on 22 Jul set `wrapPending` so a sync that read the pre-write cart could not
    // flip the box back — but it was only ever set inside orderWrapSet, and this dev base runs
    // PRODUCT mode (enable_product_level_gift_wrap is true). So the mode the store actually uses
    // never had the guard: the proxy click below writes asynchronously, cart:change fires first
    // with the old cart, syncWrapZone reads inCart=false and unticks the box under the user.
    // syncWrapZone already clears the latch as soon as a fresh cart agrees, so this needs nothing
    // else. Same guard, both modes.
    wrapPending = check.checked;
    wrapPendingAt = Date.now();
    if (check.checked) {
      var addProxy = zone.querySelector('[data-action="giftwrap-all-to-cart"]');
      if (addProxy) addProxy.click();
    } else {
      var removeProxy = zone.querySelector('[data-action="remove-gift-wrap-all"]');
      if (removeProxy) removeProxy.click();
    }
    // VERIFY PASS for the zone path too (TA-41958 round 4). The zone is the only wrap UI a
    // shopper can actually see (the per-line boxes are rendered but hidden), and its wrap-all
    // write reads the cart before writing — so an item added moments earlier can be missed
    // (read-modify-write race; Priyanka's Rs 39 for 2 products, on camera). Schedule the
    // re-level to check the outcome against zone semantics once the write settles.
    wrapQtySynced = null;
    wrapLevelTries = 0;
    clearTimeout(wrapLevelTimer);
    wrapLevelTimer = setTimeout(relevelWrap, 2500);
  });

  // cart state listeners
  document.addEventListener('cart:change', function (event) {
    if (!event.detail || !event.detail.cart || !event.detail.cart.items) return;
    lastCart = event.detail.cart;
    maybeToastAfterChange(lastCart);
    /* ⛔ THE WRAP CHECKBOX IS CORRECTED SYNCHRONOUSLY, NOT IN THE 60ms BATCH (5 Aug 2026).
       Priyanka: "whenever I unchecked the gift wrap it would briefly uncheck, then
       automatically check itself again for a moment before finally becoming unchecked."
       That middle state is this delay. The swap brings in markup rendered from the SERVER's
       cart, where the wrap is still present, so the box arrives ticked; `syncWrapZone` is what
       re-applies the shopper's pending intent, and waiting 60ms to run it leaves several
       painted frames showing the tick coming back on its own.
       syncWrapZone is idempotent and only touches classes plus one `checked` property, so
       running it here costs nothing and closes the window entirely. The rest stays batched. */
    syncWrapZone();
    /* RELEVEL ON EVERY CART CHANGE, NOT ONLY ON TOGGLE TAPS (11 Aug 2026, Priyanka's frozen
       cart: zone ticked, NO wrap line, total never moving). The verify pass was armed only by
       wrap-toggle events, so a wrap line lost to a raced write — or a product added/removed
       while the zone was on — had NOTHING watching for the mismatch, and the ticked-but-
       uncharged state persisted indefinitely. Convergent: relevelWrap no-ops the moment line
       qty == zone target, and its own corrective write re-enters here exactly once. */
    (function () {
      var zone = drawer.querySelector('[data-mcart-wrapzone]');
      if (!zone || wrapPending !== null) return;
      if (zone.getAttribute('data-mcart-wrap-mode') !== 'product') return;
      if (!zone.classList.contains('is-on')) return;
      var handle = zone.getAttribute('data-mcart-wrap-handle');
      var line = null;
      for (var i = 0; i < lastCart.items.length; i++) {
        if (lastCart.items[i].handle === handle) { line = lastCart.items[i]; break; }
      }
      var target = wrapTargetUnits(zone);
      if (line && line.quantity === target) return;   // converged — the common case, no timer churn
      clearTimeout(wrapLevelTimer);
      wrapLevelTimer = setTimeout(relevelWrap, 1200);
    })();
    // small delay: footer/prices regions are swapped synchronously, line items ~1.25s later
    setTimeout(function () {
      syncCountLabel();
      syncWrapZone();
      syncEffPanels();
      syncSwitcher();
      syncProgressLabels();
      syncRecCache();
      sanitizeGiftNote();
      renderToast();
    }, 60);
    clearTimeout(effTimer);
    effTimer = setTimeout(function () {
      syncEffPanels();
      renderToast(); // the line-items swap (~300ms) replaces the strip; re-paint the toast
    }, 500);
  });

  document.addEventListener('cart:refresh', function () {
    // the whole drawer re-renders server-side shortly after; re-read the cart for our math
    // (the MutationObserver also re-syncs the eff panels/toast the moment the swap lands)
    setTimeout(function () {
      fetchCart().then(function () {
        syncCountLabel();
        syncWrapZone();
        syncEffPanels();
        syncSwitcher();
        syncProgressLabels();
        syncRecCache();
        sanitizeGiftNote();
        renderToast();
      });
    }, 700);
  });

  // dynamic recommendation cards arrive async; keep their panels filled
  var mo = new MutationObserver(function () {
    // A qty change / cart:refresh re-renders the drawer body, and the server always renders the
    // recently-viewed block with its default `hidden` attribute. Un-hiding it on a 60ms/700ms
    // timer left a visible gap where the block collapsed then reappeared, shifting the content
    // below it (Shrey, 20 Jul 2026). Re-sync its visibility SYNCHRONOUSLY in the same tick the
    // DOM changes so there is no hidden frame. syncSwitcher is cheap + idempotent.
    syncSwitcher();   // re-assert the active tab + RV pane in the same tick as a swap (no hidden-frame gap)
    syncEmptyClass(); // empty <-> filled swap toggles the empty-only centring scope
    syncWrapZone();   // a server swap can re-render the wrap row from a stale cart; re-assert the toggle intent in the same tick (flicker fix, 22 Jul)
    syncRecCache();       // refill a swapped-in empty recommendations node in the SAME tick (flash fix, 24 Jul)
    syncProgressLabels(); // a swap can carry stale ✓ labels; re-derive from the live bar attrs
    clearTimeout(effTimer);
    effTimer = setTimeout(function () {
      syncEffPanels();
      renderToast();
    }, 120);
  });
  mo.observe(drawer, { childList: true, subtree: true });

  // PERF WAVE (25 Jul 2026): the idle init used to fetch /cart.js on EVERY pageview just to
  // pre-fill the eff panels — one of 7 duplicate cart.js hits measured at load. The fetch now
  // rides the FIRST drawer open (or the first cart:change, which carries the cart in-band);
  // eff panels appear a beat after the sheet slides in, everything else is DOM-derived.
  var cartBooted = false;
  function bootCart() {
    if (cartBooted) return;
    cartBooted = true;
    fetchCart().then(function () {
      syncEffPanels();
      syncSwitcher();
      syncProgressLabels();
      syncRecCache();
      sanitizeGiftNote();
    });
  }

  // SHEET-MOTION WINDOW (round 7, 28 Jul 2026 — Shrey 2a; supersedes round 5's permanent
  // glass). While the sheet slides open or closed, .mcart-sliding makes it FULLY OPAQUE with
  // no backdrop blur — the menu drawer's recipe, and why the menu never stalls: the GPU only
  // ever moves a solid painted layer. Glass returns via an eased hand-back AFTER settle (see
  // the SHEET MOTION block in mes-cart-v3.css, including why the 27 Jul version of this
  // failed). 550ms covers the 340ms open / 280ms close plus settle. Round 5's translucent-iOS
  // defect cannot recur: the sliding sheet is fully opaque, so there is nothing to see through.
  var slideTimer = null;
  new MutationObserver(function () {
    drawer.classList.add('mcart-sliding');
    clearTimeout(slideTimer);
    slideTimer = setTimeout(function () { drawer.classList.remove('mcart-sliding'); }, 550);
    if (drawer.hasAttribute('open')) bootCart();
  }).observe(drawer, { attributes: true, attributeFilter: ['open'] });

  document.addEventListener('cart:change', function (event) {
    // a cart:change delivers the cart in-band (the listener above stores it in lastCart) —
    // no separate boot fetch needed after that
    if (event.detail && event.detail.cart && event.detail.cart.items) cartBooted = true;
  });

  /* ══ CART FREEZE INSTRUMENT — ?cartdebug=1 (5 Aug 2026) ═══════════════════════════════════
     Priyanka, 3 Aug: the scroll freeze survives the frosted-glass fix, it is worse on Android,
     and — the useful part — it has NO stable trigger: "sometimes after adding Gift Wrap, closing
     and reopening; at other times removing the Free Gift, closing and reopening."
     Both of her triggers are the same SHAPE: a cart mutation, then close, then reopen. So the
     residual is the cost of the re-render landing on the next open, not the blur.

     ⛔ WHY AN ON-DEVICE INSTRUMENT AND NOT MORE THEORY: my environment cannot measure duration
     at all (hidden pane, no animation frames), and four rounds of reasoning from code produced
     fixes for a path her phone does not even take. This measures the freeze where it happens.

     THE KEY NUMBER IS `gap` — the longest interval between animation frames after the drawer
     opens. That IS the freeze the shopper feels: while frames stop, scrolling is dead but taps
     still queue, which is exactly the signature she described on 28 Jul. Alongside it:
     `lt` = the longest blocking task the browser itself reports (PerformanceObserver longtask),
     `since` = seconds from the last cart mutation to this open (her "sometimes" variable), and
     the cart shape at that moment (lines, units, wrap, DOM nodes in the drawer).
     Everything is gated behind the query param — no shopper ever pays a byte of it. */
  if (location.search.indexOf('cartdebug=1') > -1) (function cartDebug() {
    var log = [], lastMutation = 0, lastMutationKind = '-', maxLongTask = 0, panel = null;
    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (e) { if (e.duration > maxLongTask) maxLongTask = e.duration; });
      }).observe({ entryTypes: ['longtask'] });
    } catch (e) { /* Safari/older Android: gap measurement below still works */ }

    function paint() {
      if (!panel) {
        panel = document.createElement('div');
        panel.setAttribute('style', [
          'position:fixed', 'left:6px', 'bottom:78px', 'z-index:2147483647',
          'background:rgba(0,0,0,.88)', 'color:#0f0', 'font:11px/1.35 ui-monospace,Menlo,monospace',
          'padding:6px 8px', 'border-radius:6px', 'max-width:94vw', 'white-space:pre',
          'pointer-events:none'
        ].join(';'));
        document.body.appendChild(panel);
      }
      panel.textContent = 'CART DEBUG — open the cart, then try to scroll\n' + log.slice(-7).join('\n');
    }

    function shape() {
      var lines = drawer.querySelectorAll('[data-mcart-line], .mcart-line').length;
      var units = (lastCart && lastCart.item_count) || 0;
      var wrap = !!(lastCart && window.MES_WRAP_HANDLE && lastCart.items &&
                    lastCart.items.some(function (i) { return i.handle === window.MES_WRAP_HANDLE; }));
      return lines + 'ln ' + units + 'u' + (wrap ? ' +wrap' : '') + ' ' + drawer.querySelectorAll('*').length + 'n';
    }

    /* Watch frames for 3.5s after every open. The longest gap is the freeze; anything over
       ~120ms is a stall the shopper can feel, and her reports are 2000-3000ms. */
    function watchOpen() {
      var t0 = performance.now(), prev = t0, gap = 0, frames = 0, done = false, ltStart = maxLongTask;
      var since = lastMutation ? ((t0 - lastMutation) / 1000).toFixed(1) + 's after ' + lastMutationKind : 'no recent change';
      function finish(tag) {
        if (done) return;
        done = true;
        log.push('open  gap=' + Math.round(gap) + 'ms  lt=' + Math.round(maxLongTask - ltStart) +
                 'ms  ' + shape() + '  (' + since + ')' + (tag || ''));
        paint();
      }
      (function frame(now) {
        frames++;
        gap = Math.max(gap, now - prev);
        prev = now;
        if (now - t0 < 3500) { requestAnimationFrame(frame); } else { finish(); }
      })(t0);
      /* ⛔ BACKSTOP, and it is not optional. The frame loop is the measurement AND the trigger,
         so a device that stops painting entirely — the very failure being measured — would never
         emit its line. Verified on my own instrument: a hidden browser pane runs no animation
         frames, and the reading silently never arrived. A wall-clock timer always lands, and
         `frames=N` says how much to trust the gap. */
      setTimeout(function () { finish('  frames=' + frames); }, 4000);
    }

    /* ══ THE SWIPE PROBE (5 Aug 2026, after Priyanka's first cartdebug reading) ═══════════════
       Her numbers killed the model I had: three opens, one of them 1.2s after a cart change,
       and `gap` was 78-89ms with `lt=0ms`. Frames never stopped. So the freeze is NOT a
       rendering stall and not a long task — the compositor was alive the whole time, which
       means every fix aimed at render cost was aimed at the wrong thing.

       What is left is the input path: the finger moves and the list does not. This probe
       measures exactly that on the scroller itself — how far the finger travelled, how far the
       list actually moved, and how many of those touchmove events had their default action
       CANCELLED by some other listener (the theme's own GestureArea calls preventDefault on
       touchmove, passive:false, whenever the horizontal delta from its remembered start point
       exceeds 10px — a stale remembered point would cancel every move).

         dy 280px · moved 0px · pd 14/14  → input is being swallowed (a logic bug)
         dy 280px · moved 240px           → it scrolled; the freeze is elsewhere
         dy 280px · moved 0px · pd 0/14   → not preventDefault; look at overflow/geometry, which
                                            this line also prints so all three are settled at once

       ⛔ The listener is added LAST and passive, so it observes `defaultPrevented` after every
       other handler has had its say, and cannot itself change the behaviour it is measuring. */
    /* ── ROUND 2 OF THE PROBE (5 Aug 2026, after her SWIPE DEAD screenshots) ──────────────────
       Six dead swipes, and two findings that redirect this again:
         • `pd=0` on every one. NOTHING is calling preventDefault. The GestureArea theory is
           DEAD — do not revisit it.
         • Four of them read `pd=0/1`: a 119-166px finger travel delivered exactly ONE touchmove.
           A real swipe delivers dozens. Events stopping after the first move is the signature of
           the touch TARGET being destroyed mid-gesture — which is precisely what the drawer's
           re-render does, and the re-render lands right when she swipes "straight away" after
           reopening. The other two read `0/9`: plenty of moves, still no movement, which is a
           scroller that is not scrollable AT THAT MOMENT.
       Both sub-cases say the same thing: she is swiping while the drawer is being rebuilt. So
       this round measures the rebuild directly — `mut` counts childList mutations inside the
       drawer DURING her swipe, and `H` is the scrollable height at touchstart.

       ⛔ ALSO: the line is now SHORT. Her last screenshot cut off `scrollable=` at the screen
       edge, which was the number that would have settled the 0/9 case — my instrument hid its
       own key field. Field names are one letter and the deciding ones come first. */
    var mutCount = 0;
    new MutationObserver(function (recs) { mutCount += recs.length; })
      .observe(drawer, { childList: true, subtree: true });

    var probeBound = false;
    function bindSwipeProbe() {
      if (probeBound) return;
      var sc = null;
      try { sc = drawer.shadowRoot && drawer.shadowRoot.querySelector('[part="body"]'); } catch (e) {}
      if (!sc) return;
      probeBound = true;
      /* ── ROUND 3 (5 Aug 2026, after her H=955 mut=0 screenshots) ───────────────────────────
         Her data killed the rebuild theory as well: on the DEAD swipes the scroller was
         **scrollable the whole time (H=955)**, the DOM was **not** being rebuilt (`mut=0`),
         nothing was cancelled (`pd=0`) and the touched node never went `GONE`. Two theories
         dead in two rounds; that is what a good instrument is for.

         The one thing left in her numbers, and it inverts: the swipes that WORKED delivered
         3-4 touchmoves and scrolled 48-182px. The DEAD ones delivered **10-17** touchmoves and
         scrolled zero. When the compositor drives a scroll it hands JS few events; when JS gets
         many and nothing moves, the browser has given the gesture to something else.

         The cart contains nested scrollables — the SUGGESTED / RECENTLY VIEWED carousel
         (`.mcart-hs`, `overflow-x:auto`) and the gift-note `textarea`, both of which fill the
         screen in her two screenshots exactly where the dead swipes happened. A vertical drag
         starting inside one of those is consumed by it. There is precedent: on 24 Jul she
         reported "can't scroll up or down over the recommended products" and the fix was
         widening `touch-action` on that same row.

         So round 3 names the element under her finger and reports whether any INNER scroller
         moved instead. `on=` is the answer by itself. */
      var y0 = 0, top0 = 0, moves = 0, pd = 0, dy = 0, h0 = 0, mut0 = 0, node0 = null;
      var innerEl = null, ix0 = 0, iy0 = 0, onLabel = '';
      function labelFor(el) {
        for (var n = el; n && n !== sc; n = n.parentElement) {
          var c = (n.className && n.className.baseVal !== undefined) ? n.className.baseVal : (n.className || '');
          var m = String(c).match(/mcart-[a-z-]+/);
          if (m) return m[0];
          if (n.tagName === 'TEXTAREA' || n.tagName === 'INPUT' || n.tagName === 'BUTTON') return n.tagName.toLowerCase();
        }
        return 'body';
      }
      function innerScrollerFor(el) {
        for (var n = el; n && n !== sc; n = n.parentElement) {
          if (n.scrollHeight > n.clientHeight + 2 || n.scrollWidth > n.clientWidth + 2) return n;
        }
        return null;
      }
      /* ⛔ FRAME GAP MEASURED **DURING THE SWIPE** (5 Aug 2026 — Shrey's push-back, and he was
         right to make it). I concluded "the compositor never stalls" from the `gap` field, but
         that field measures the 3.5s window after the drawer OPENS, which is not when she
         swipes. A freeze that starts later, or lasts longer than that window, is invisible to
         it — so "frames were fine" was a claim about a different moment than the one that
         failed. This closes the gap: `fg` is the longest interval between animation frames
         BETWEEN her touchstart and touchend, on the swipe that died.
           fg small + m=0  → frames are running and the scroll is being refused (input routing)
           fg large + m=0  → the pipeline really is stalled while she drags (compositor/raster)
         Two very different bugs that the old field could not separate. */
      var fgMax = 0, fgPrev = 0, fgOn = false, selHit = 0, dySigned = 0;
      function fgTick(now) {
        if (!fgOn) return;
        if (fgPrev) fgMax = Math.max(fgMax, now - fgPrev);
        fgPrev = now;
        requestAnimationFrame(fgTick);
      }
      sc.addEventListener('touchstart', function (e) {
        y0 = e.touches[0].clientY; top0 = sc.scrollTop; moves = 0; pd = 0; dy = 0; dySigned = 0;
        h0 = sc.scrollHeight - sc.clientHeight;
        mut0 = mutCount;
        node0 = e.target;
        onLabel = labelFor(e.target);
        innerEl = innerScrollerFor(e.target);
        ix0 = innerEl ? innerEl.scrollLeft : 0;
        iy0 = innerEl ? innerEl.scrollTop : 0;
        fgMax = 0; fgPrev = 0; selHit = 0; fgOn = true; requestAnimationFrame(fgTick);
      }, { passive: true });
      /* `sel` — was the browser in a TEXT SELECTION when the swipe died? (6 Aug 2026)
         Her latest reading forces this: fg=17ms (a steady 60fps), pd=0/106 (one hundred and six
         touchmoves, none cancelled), scroller scrollable, nothing rebuilt, and zero pixels of
         scroll. The page is drawing perfectly and simply refusing to pan.
         On Android, once a long-press starts a text selection, dragging EXTENDS the selection
         instead of scrolling — the page keeps receiving touchmoves and never moves, taps keep
         working, and it clears when you tap elsewhere. Every symptom she has reported, including
         "it clears after a few seconds", matches that.
         And it explains the split her own data shows: dead swipes start `on=mcart-li`, which is
         dense TEXT (title, variant, price); the swipe that worked started `on=mcart-hs`, the
         image carousel. If `sel=Y` on the dead lines, the fix is `user-select: none` on the line
         rows — one CSS rule. DERIVED until her screenshot says otherwise. */
      sc.addEventListener('touchmove', function (e) {
        moves++;
        if (e.defaultPrevented) pd++;
        try {
          var s = window.getSelection();
          if (s && s.rangeCount && !s.isCollapsed) selHit++;
        } catch (err) {}
        var d = e.touches[0].clientY - y0;
        if (Math.abs(d) > Math.abs(dySigned)) dySigned = d;
        dy = Math.max(dy, Math.abs(d));
      }, { passive: true });
      function report() {
        fgOn = false;
        if (dy < 30) return;                       /* a tap, not a swipe attempt — ignore */
        var moved = Math.abs(sc.scrollTop - top0);
        var inner = innerEl
          ? ' in=' + labelFor(innerEl) + ':' + Math.round(innerEl.scrollTop - iy0) + '/' + Math.round(innerEl.scrollLeft - ix0)
          : ' in=none';
        /* ⛔ `at` AND `dir` — the two fields I should have had from the first probe (6 Aug 2026).
           Every DEAD line so far recorded |distance| only, so none of them can distinguish
             "the browser refused to scroll"           (a bug)
           from
             "she was at the top and pulled downward"  (correct behaviour, nothing to scroll).
           A reopened drawer starts at scrollTop 0, and pulling DOWN there moves nothing by
           design — with every touchmove still delivered, no preventDefault, and frames at 60fps,
           which is EXACTLY the fingerprint I have been calling a freeze for two days.
           `at` is the scroll position when her finger landed, `H` the scrollable range, `dir`
           which way she pulled. `at=0 dir=down` is not a defect; `at=340 dir=up` is. */
        var atEnd = (dySigned > 0 && top0 <= 0) || (dySigned < 0 && top0 >= h0 - 1);
        log.push((moved === 0 ? (atEnd ? '·edge ' : '⛔DEAD ') : 'ok    ') +
                 'at=' + Math.round(top0) + '/' + h0 + ' dir=' + (dySigned > 0 ? 'down' : 'up') +
                 ' fg=' + Math.round(fgMax) + 'ms sel=' + (selHit ? 'Y' + selHit : 'n') +
                 ' on=' + onLabel + inner +
                 ' f=' + Math.round(dy) + ' m=' + Math.round(moved) + ' pd=' + pd + '/' + moves +
                 ' mut=' + (mutCount - mut0) + (node0 && !node0.isConnected ? ' GONE' : ''));
        paint();
        dy = 0;
      }
      sc.addEventListener('touchend', report, { passive: true });
      /* touchcancel is the other half of the story: if the gesture is taken away rather than
         finished, touchend never fires and the swipe would go unrecorded entirely. */
      sc.addEventListener('touchcancel', function () { dy = Math.max(dy, 31); report(); }, { passive: true });
    }
    bindSwipeProbe();

    new MutationObserver(function () {
      if (drawer.hasAttribute('open')) { bindSwipeProbe(); watchOpen(); }
      else { log.push('close'); paint(); }
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });

    ['cart:change', 'cart:refresh'].forEach(function (evt) {
      document.addEventListener(evt, function () {
        lastMutation = performance.now();
        lastMutationKind = evt.split(':')[1];
        log.push(evt.split(':')[1] + '  ' + shape());
        paint();
      });
    });
    /* name the wrap / free-gift actions so "which action preceded the freeze" is answerable */
    drawer.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('[data-action]') : null;
      if (t) { lastMutationKind = t.getAttribute('data-action'); lastMutation = performance.now(); }
    }, true);
    paint();
  })();

  // initial state (idle, DOM-only — no network)
  function init() {
    if (drawer.hasAttribute('open')) { bootCart(); return; }
    syncSwitcher();
    syncProgressLabels();
    syncRecCache();
    sanitizeGiftNote();
  }
  if ('requestIdleCallback' in window) {
    requestIdleCallback(init, { timeout: 4000 });
  } else {
    setTimeout(init, 1500);
  }
})();
